/**
 * DailyCheckIn — "What happened today?" one-tap dashboard prompt.
 * =========================================================================
 *
 * Shown at the top of the caregiver Dashboard once per day. Caregiver
 * picks ONE of eight situation codes; the backend returns a scripted
 * Lane response with:
 *   - next steps
 *   - what to track
 *   - when to call the doctor
 *   - a Clarity Call CTA (except for "nothing major")
 *
 * The prompt hides for the rest of the day after any selection. Ships
 * with no modal — everything renders inline on the dashboard so it never
 * interrupts the caregiver's flow.
 */
import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Sparkles, MessageCircle, PhoneCall, Clock, ClipboardList, HeartPulse, CheckCircle2, Activity, ShieldAlert, Pill, Utensils, Moon, CalendarDays, CloudRain, Smile } from "lucide-react";
import { track, EVENTS } from "@/lib/analytics";

const OPTIONS = [
    { code: "behavior_change",       label: "Behavior feels different",    icon: Activity },
    { code: "fall_or_safety",        label: "Fall or safety concern",      icon: ShieldAlert },
    { code: "medication_question",   label: "Medication question",         icon: Pill },
    { code: "eating_drinking",       label: "Eating or drinking change",   icon: Utensils },
    { code: "sleep_issue",           label: "Sleep issue",                 icon: Moon },
    { code: "appointment_coming",    label: "Appointment coming up",       icon: CalendarDays },
    // "I feel overwhelmed" removed (Jul 2026, founder): redundant with the
    // caregiver check-in above — it split the data.
    { code: "nothing_major",         label: "Nothing major today",         icon: Smile },
];

export default function DailyCheckIn() {
    const [today, setToday] = useState(null);       // { logged_today, response? }
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [picked, setPicked] = useState(null);

    useEffect(() => {
        let active = true;
        api.get("/me/daily-checkin/today")
            .then(r => { if (active) setToday(r.data || { logged_today: false }); })
            .catch(() => { if (active) setToday({ logged_today: false }); })
            .finally(() => { if (active) setLoading(false); });
        return () => { active = false; };
    }, []);

    const submit = async (code) => {
        setSubmitting(true);
        try {
            const r = await api.post("/me/daily-checkin", { code });
            setToday({
                logged_today: true,
                code,
                label: r.data?.response?.label,
                response: r.data?.response || null,
                created_at: new Date().toISOString(),
            });
            try {
                track(EVENTS.DAILY_CHECK_IN_COMPLETED, { code });
            } catch (_e) { /* analytics best-effort */ }
        } catch (_e) {
            // Silent — the caregiver can retry; we don't nag with an error toast
            // on the dashboard because the check-in is voluntary.
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) return null;

    // Already submitted today → render Lane's response (compact, calm).
    if (today?.logged_today && today?.response) {
        return <DailyCheckInResponse today={today} />;
    }

    // Fresh prompt.
    return (
        <section
            data-testid="daily-checkin-prompt"
            className="rounded-2xl border border-[#E4E8EC] bg-white p-5 md:p-6 shadow-[0_1px_2px_rgba(16,42,67,0.04)]"
        >
            <div className="flex items-start gap-3">
                <span className="w-11 h-11 rounded-2xl bg-teal-soft flex items-center justify-center shrink-0">
                    <Sparkles size={18} strokeWidth={1.8} className="text-teal-dark" />
                </span>
                <div className="flex-1 min-w-0">
                    <h3
                        data-testid="daily-checkin-heading"
                        className="font-heading text-xl md:text-2xl text-[#2D3748] leading-snug"
                    >
                        How is today going?
                    </h3>
                    <p className="mt-1 text-sm text-[#4C5551]">
                        Choose what&apos;s closest. Lane can help you decide what to track and what may need attention.
                    </p>
                    <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2.5" data-testid="daily-checkin-options">
                        {OPTIONS.map(opt => {
                            const active = picked === opt.code;
                            return (
                                <button
                                    key={opt.code}
                                    type="button"
                                    onClick={() => { setPicked(opt.code); submit(opt.code); }}
                                    disabled={submitting}
                                    aria-pressed={active}
                                    data-testid={`daily-checkin-option-${opt.code}`}
                                    className={`flex items-center gap-2.5 text-left text-sm min-h-[48px] rounded-2xl px-3.5 py-2.5 border transition-all disabled:opacity-60 ${active
                                        ? "bg-teal-dark text-white border-teal-dark shadow-sm"
                                        : "bg-white text-[#2D3748] border-[#E4E8EC] hover:border-teal hover:bg-teal-soft/40 hover:shadow-sm"}`}
                                >
                                    <span className={`w-8 h-8 rounded-xl flex items-center justify-center shrink-0 ${active ? "bg-white/20" : "bg-[#F3F8F5]"}`}>
                                        <opt.icon size={15} strokeWidth={2} className={active ? "text-white" : "text-teal-dark"} />
                                    </span>
                                    {opt.label}
                                </button>
                            );
                        })}
                    </div>
                </div>
            </div>
        </section>
    );
}

/**
 * Lane's response — brief summary card with a modal for the full guidance.
 * The dashboard should never expand into a very long inline card.
 * Exported: FirstVisitCheckIn renders the SAME component so a first visit
 * and a reload can never show different clinical content.
 */
export function DailyCheckInResponse({ today }) {
    const [open, setOpen] = useState(false);
    const r = today.response;
    if (!r) return null;

    return (
        <section
            data-testid="daily-checkin-response"
            className="ml-card p-5 md:p-6 border-l-4 border-l-teal-dark bg-cream-light/50"
        >
            <div className="flex items-start gap-3">
                <span className="w-11 h-11 rounded-2xl bg-white flex items-center justify-center shrink-0">
                    <MessageCircle size={18} strokeWidth={1.8} className="text-teal-dark" />
                </span>
                <div className="flex-1 min-w-0">
                    <p className="text-[11px] uppercase tracking-wider font-semibold text-teal-dark">
                        Today&apos;s check-in · Lane responded
                    </p>
                    <h3
                        data-testid="daily-checkin-response-label"
                        className="mt-1 font-heading text-base md:text-lg text-[#1F2624] leading-snug"
                    >
                        You picked: <span className="text-teal-dark">{today.label || r.label}</span>
                    </h3>
                    <p
                        data-testid="daily-checkin-response-headline"
                        className="mt-2 text-[#1F2624] leading-relaxed"
                    >
                        {r.headline}
                    </p>
                    <button
                        type="button"
                        onClick={() => setOpen(true)}
                        data-testid="daily-checkin-toggle"
                        className="mt-3 inline-flex items-center gap-1.5 rounded-2xl bg-teal-dark text-white text-xs font-semibold px-3.5 py-2 hover:brightness-[1.05] transition"
                    >
                        See Lane&apos;s guidance
                    </button>
                </div>
            </div>

            {open && (
                <div
                    data-testid="daily-checkin-guidance-modal"
                    role="dialog"
                    aria-modal="true"
                    className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-[#1F2624]/40 backdrop-blur-sm p-0 sm:p-6"
                    onClick={() => setOpen(false)}
                >
                    <div
                        className="w-full sm:max-w-lg max-h-[85vh] overflow-y-auto rounded-t-3xl sm:rounded-3xl bg-white shadow-xl border border-[#E2E8F0] p-5 md:p-6"
                        onClick={e => e.stopPropagation()}
                    >
                        <div className="flex items-start justify-between gap-3 mb-3">
                            <div>
                                <p className="text-[11px] uppercase tracking-wider font-semibold text-teal-dark">Lane&apos;s guidance</p>
                                <h4 className="mt-0.5 font-heading text-lg text-[#1F2624] leading-snug">{today.label || r.label}</h4>
                            </div>
                            <button
                                type="button"
                                onClick={() => setOpen(false)}
                                data-testid="daily-checkin-guidance-close"
                                className="text-sm text-[#4C5551] hover:text-[#1F2624] underline decoration-dotted underline-offset-4"
                            >
                                Close
                            </button>
                        </div>
                        <p className="text-[#1F2624] leading-relaxed">{r.headline}</p>
                        <div className="mt-4 space-y-4">
                            {r.next_steps?.length > 0 && (
                                <ResponseBlock testid="daily-checkin-next-steps" title="Next steps" icon={ClipboardList} items={r.next_steps} />
                            )}
                            {r.what_to_track?.length > 0 && (
                                <ResponseBlock testid="daily-checkin-what-to-track" title="What to track" icon={Clock} items={r.what_to_track} />
                            )}
                            {r.when_to_call_doctor?.length > 0 && (
                                <ResponseBlock testid="daily-checkin-when-to-call" title="When to call the doctor" icon={HeartPulse} items={r.when_to_call_doctor} tone="warn" />
                            )}
                            {!r.clarity_call && (
                                <p data-testid="daily-checkin-nothing-major" className="text-sm text-[#4C5551] italic pt-1">
                                    Enjoy the steady day. See you tomorrow.
                                </p>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </section>
    );
}

function ResponseBlock({ testid, title, icon: Icon, items, tone = "neutral" }) {
    const iconTone = tone === "warn" ? "text-coral-dark" : "text-teal-dark";
    return (
        <div data-testid={testid}>
            <p className="text-[11px] uppercase tracking-wider font-semibold text-[#4C5551] flex items-center gap-1.5 mb-2">
                <Icon size={13} strokeWidth={1.8} className={iconTone} />
                {title}
            </p>
            <ul className="space-y-1.5">
                {items.map((s, i) => (
                    <li
                        key={i}
                        className="flex items-start gap-2 text-sm text-[#1F2624] leading-relaxed"
                    >
                        <CheckCircle2 size={13} strokeWidth={2} className={`mt-1 shrink-0 ${iconTone}`} />
                        <span>{s}</span>
                    </li>
                ))}
            </ul>
        </div>
    );
}

// Named export for tests / direct imports.
export { PhoneCall, OPTIONS };
